import { Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import { useQueryClient } from '@tanstack/react-query';
import { i18nKeys, useTranslation } from 'locales';
import { isNullish } from 'remeda';
import MoveRecoveryPlanInvoicesModal from 'shared/action-component/MoveRecoveryPlanInvoices/MoveRecoveryPlanInvoicesModal';
import ChooseRecoveryPlanModal from 'shared/components/ChooseRecoveryPlanModal';
import { useLegacyAxiosInstance } from 'shared/hooks/utils';
import {
  fetchRecoveryPlanSteps,
  type RecoveryPlanSummary,
  type StepsSwitch,
  useSwitchRecoveryPlanInvoices,
  useUpdateDefaultRecoveryPlan,
} from '../api';

type StepMapping = {
  title: string;
  fromPlanId: string;
  toPlanId: string;
  companyDefault: boolean;
  onSubmit: (stepsSwitch: StepsSwitch) => void;
};

const closeModals = { onSuccess: () => modals.closeAll() };

export function useRecoveryPlanActions(plans: RecoveryPlanSummary[]) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const legacyAxiosInstance = useLegacyAxiosInstance();
  const { updateDefaultRecoveryPlan } = useUpdateDefaultRecoveryPlan();
  const { switchRecoveryPlanInvoices } = useSwitchRecoveryPlanInvoices();

  const currentDefault = plans.find(plan => plan.isDefault);

  const loadPlanSteps = (id: string, companyDefault: boolean) =>
    queryClient.fetchQuery({
      queryKey: ['recoveryPlanSteps', id, companyDefault],
      queryFn: () => fetchRecoveryPlanSteps(legacyAxiosInstance, { id, companyDefault }),
      staleTime: 0,
    });

  const openStepMapping = async ({
    title,
    fromPlanId,
    toPlanId,
    companyDefault,
    onSubmit,
  }: StepMapping) => {
    const [oldPlan, newPlan] = await Promise.all([
      loadPlanSteps(fromPlanId, companyDefault),
      loadPlanSteps(toPlanId, companyDefault),
    ]);

    modals.open({
      title,
      size: '90%',
      children: (
        <MoveRecoveryPlanInvoicesModal oldPlan={oldPlan} newPlan={newPlan} onSubmit={onSubmit} />
      ),
    });
  };

  const setAsDefault = (plan: RecoveryPlanSummary) => {
    const makeDefault = (stepsSwitch?: StepsSwitch) =>
      updateDefaultRecoveryPlan({ recoveryPlanId: plan.id, stepsSwitch }, closeModals);

    if (isNullish(currentDefault) || currentDefault.invoicesCount === 0) {
      makeDefault();
      return;
    }

    modals.openConfirmModal({
      title: t(i18nKeys.SET_DEFAULT_PLAN),
      children: <Text size="sm">{t(i18nKeys.PLAN.MOVE_INVOICES_IN_NEW_PLAN)}</Text>,
      labels: { confirm: t(i18nKeys.YES), cancel: t(i18nKeys.NO) },
      onConfirm: () =>
        openStepMapping({
          title: t(i18nKeys.SET_DEFAULT_PLAN),
          fromPlanId: currentDefault.id,
          toPlanId: plan.id,
          companyDefault: true,
          onSubmit: makeDefault,
        }),
      onCancel: () => makeDefault(),
    });
  };

  const moveInvoices = (plan: RecoveryPlanSummary) =>
    modals.open({
      title: t(i18nKeys.PLAN.WHERE_MOVE_INVOICES),
      children: (
        <ChooseRecoveryPlanModal
          recoverPlans={plans}
          onClick={toPlanId => {
            if (isNullish(toPlanId)) return;

            modals.closeAll();
            openStepMapping({
              title: t(i18nKeys.PLAN.INVOICES),
              fromPlanId: plan.id,
              toPlanId,
              companyDefault: false,
              onSubmit: stepsSwitch =>
                switchRecoveryPlanInvoices(
                  { fromPlanId: plan.id, toPlanId, stepsSwitch },
                  closeModals,
                ),
            });
          }}
        />
      ),
    });

  return { setAsDefault, moveInvoices };
}
